<template>
  <div>
    <NavBar />
    <div class="container">
      <div class="row">
        <div class="col-md-auto align-self-center pr-0 pb-1">
          <span @click="onStarClick()">
            <fa class="gold-star" :icon="['fas', 'star']" fixed-width size="lg" v-if="report && report.isStarred" />
            <fa :icon="['far', 'star']" fixed-width size="lg" v-if="!report || (report && !report.isStarred)" />
          </span>
        </div>
        <div class="col pl-2">
          <h1>
            {{ title }}
            <span class="badge rounded mx-2" :class="getStatusClass(statusCode)">{{ statusDisplayName }}</span>
          </h1>
        </div>
        <div class="col d-flex py-2 d-print-none">
          <div class="ml-auto">
            <Comments
              :isLoading="isLoading"
              :organizationId="organizationId"
              :reportId="reportId"
              :report="report"
              subjectType="report"
              :subjectId="reportId"
              @reload="load"
              @error="$emit('error')"
            />
            <ExcelExportButton class="ml-2" :organizationId="organizationId" :reportId="reportId" v-if="isVerensicsAdministrator" />
            <PdfExportButton class="ml-2" :organizationId="organizationId" :reportId="reportId" />
          </div>
        </div>
      </div>
      <div v-if="hasInvestigationId" class="row">
        <div class="col">
          <h6>Investigation Reference Code: {{ this.report.registration.investigationId }}</h6>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div v-if="successMessage" class="alert alert-success alert-dismissible fade show mt-4" role="alert">
            {{ successMessage }}
            <button type="button" class="close" @click="successMessage = null" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <ErrorSummary :error="error" />
        </div>
      </div>
      <div class="row mt-4" v-if="showRequestForContact">
        <div class="col">
          <RequestForContact :report="report" :isLoading="isLoading" />
        </div>
      </div>
      <div class="row mt-4">
        <div class="col">
          <Registration :report="report" :isLoading="isLoading" />
        </div>
      </div>
      <div class="row mt-4">
        <div class="col">
          <Summary
            :organizationId="organizationId"
            :reportId="reportId"
            :report="report"
            :isLoading="isLoading"
            @loading="onLoading"
            @success="onSuccess"
            @error="onError"
          />
        </div>
      </div>
      <div class="row mt-4" v-if="!hasData && !isLoading">
        <div class="col">
          <div class="alert alert-danger font-weight-bolder" style="font-size: 1.1rem" role="alert">
            {{ noDataText }}
          </div>
        </div>
      </div>
      <div class="row mt-4 d-print-none" v-if="hasData">
        <div class="col">
          <button
            type="button"
            class="btn btn-block"
            :class="{ 'btn-primary': isHighlightMode, 'btn-outline-primary': !isHighlightMode }"
            @click="mode = 'highlight'"
            data-bs-toggle="tooltip"
            data-bs-delay="500"
            :title="getHighlightTabMouseOver"
          >
            {{ getHighlightTabLabel }}
          </button>
        </div>
        <div class="col">
          <button
            type="button"
            class="btn btn-block"
            :class="{ 'btn-primary': isFullMode, 'btn-outline-primary': !isFullMode }"
            @click="mode = 'full'"
            data-bs-toggle="tooltip"
            data-bs-delay="500"
            title="View the full report"
          >
            Full Report
          </button>
        </div>
        <div class="col">
          <button
            type="button"
            class="btn btn-block"
            :class="{ 'btn-primary': isObservationMode, 'btn-outline-primary': !isObservationMode }"
            @click="mode = 'observation'"
            data-bs-toggle="tooltip"
            data-bs-delay="500"
            title="View the interviewee's observations in the workplace"
          >
            Observations
          </button>
        </div>
        <div class="col">
          <button
            type="button"
            class="btn btn-block"
            :class="{ 'btn-primary': isResearchMode, 'btn-outline-primary': !isResearchMode }"
            @click="mode = 'research'"
            data-bs-toggle="tooltip"
            data-bs-delay="500"
            title="View the findings in your company's research projects"
          >
            Research Questions
          </button>
        </div>
        <div v-if="showInvestigationTab" class="col">
          <button
            type="button"
            class="btn btn-block"
            :class="{ 'btn-primary': isInvestigationMode, 'btn-outline-primary': !isInvestigationMode }"
            @click="mode = 'investigations'"
            data-bs-toggle="tooltip"
            data-bs-delay="500"
            title="View the findings in your company's investigations"
          >
            Investigations
          </button>
        </div>
      </div>
      <div class="row my-4" v-if="hasData">
        <div class="col">
          <Consistency v-if="isHighlightMode" :isLoading="isLoading" :organizationId="organizationId" :reportId="reportId" :report="report" />
          <RiskIndex v-if="isFullMode" :isLoading="isLoading" :organizationId="organizationId" :reportId="reportId" :report="report" />
        </div>
      </div>
      <div class="row my-4" v-if="hasData">
        <div class="col">
          <RiskIndexHighlightsDetail
            v-if="isHighlightMode"
            :organizationId="organizationId"
            :reportId="reportId"
            :report="report"
            :isLoading="isLoading"
            @loading="onLoading"
            @success="onSuccess"
            @error="onError"
            @reload="load"
          />
          <RiskIndexFullDetail
            v-if="isFullMode"
            :organizationId="organizationId"
            :reportId="reportId"
            :report="report"
            :isLoading="isLoading"
            @loading="onLoading"
            @success="onSuccess"
            @error="onError"
            @reload="load"
          />
          <RiskIndexObservationsDetail
            v-if="isObservationMode"
            :organizationId="organizationId"
            :reportId="reportId"
            :report="report"
            :isLoading="isLoading"
            @loading="onLoading"
            @success="onSuccess"
            @error="onError"
            @reload="load"
          />
          <RiskIndexResearchDetail
            v-if="isResearchMode"
            :organizationId="organizationId"
            :reportId="reportId"
            :report="report"
            :isLoading="isLoading"
            @loading="onLoading"
            @success="onSuccess"
            @error="onError"
            @reload="load"
          />
          <RiskIndexInvestigationDetail
            v-if="isInvestigationMode"
            :organizationId="organizationId"
            :reportId="reportId"
            :report="report"
            :isLoading="isLoading"
            @loading="onLoading"
            @success="onSuccess"
            @error="onError"
            @reload="load"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ErrorSummary from "@/components/ErrorSummary.vue";
import NavBar from "@/views/reports/NavBar.vue";
import ExcelExportButton from "@/views/reports/report/edit-report/ExcelExportButton.vue";
import PdfExportButton from "@/views/reports/report/edit-report/PdfExportButton.vue";
import Registration from "@/views/reports/report/edit-report/Registration.vue";
import Summary from "@/views/reports/report/edit-report/Summary.vue";
import Consistency from "@/views/reports/report/edit-report/Consistency.vue";
import RiskIndex from "@/views/reports/report/edit-report/RiskIndex.vue";
import RiskIndexHighlightsDetail from "@/views/reports/report/edit-report/RiskIndexHighlightsDetail.vue";
import RiskIndexFullDetail from "@/views/reports/report/edit-report/RiskIndexFullDetail.vue";
import RiskIndexObservationsDetail from "@/views/reports/report/edit-report/RiskIndexObservationsDetail.vue";
import RiskIndexResearchDetail from "@/views/reports/report/edit-report/RiskIndexResearchDetail.vue";
import RiskIndexInvestigationDetail from "@/views/reports/report/edit-report/RiskIndexInvestigationDetail.vue";
import Comments from "@/views/reports/report/edit-report/Comments.vue";
import RequestForContact from "@/views/reports/report/edit-report/RequestForContact.vue";

export default {
  components: {
    NavBar,
    ErrorSummary,
    ExcelExportButton,
    PdfExportButton,
    Registration,
    Summary,
    Consistency,
    RiskIndex,
    RiskIndexHighlightsDetail,
    RiskIndexFullDetail,
    RiskIndexObservationsDetail,
    RiskIndexResearchDetail,
    RiskIndexInvestigationDetail,
    Comments,
    RequestForContact,
  },
  props: {
    organizationId: String,
    reportId: String,
  },
  data() {
    return {
      loadingMessage: null,
      successMessage: null,
      error: null,

      mode: "highlight",

      report: null,
    };
  },
  computed: {
    ...mapGetters("role", ["isVerensicsAdministrator"]),
    ...mapGetters("organization", ["organization"]),
    isLoading() {
      return !!this.loadingMessage;
    },
    isHighlightMode() {
      return this.mode == "highlight";
    },
    isFullMode() {
      return this.mode == "full";
    },
    isObservationMode() {
      return this.mode == "observation";
    },
    isResearchMode() {
      return this.mode == "research";
    },
    isInvestigationMode() {
      return this.mode == "investigations";
    },
    showInvestigationTab() {
      return this.report && this.report.registration && this.report.registration.testCode != "investigation";
    },
    showRequestForContact() {
      return this.report && this.report.hasRequestForContact;
    },
    statusCode() {
      if (this.report && this.report.statusCode) return this.report.statusCode;
      return "unknown";
    },
    statusDisplayName() {
      if (this.report && this.report.statusDisplayName) return this.report.statusDisplayName;
      return "Unknown";
    },
    hasInvestigationId() {
      return this.report && this.report.registration && this.report.registration.investigationId;
    },
    hasData() {
      if (this.report && this.report.summary && (this.report.summary.scoreCode === "inconclusive" || this.report.statusCauseCode === "timeout")) return false;
      if (this.statusCode === "completed") return true;
      return false;
    },
    noDataText() {
      if (this.report && this.report.statusCauseCode === "timeout")
        return "The test taker allowed too many questions to time out to generate an accurate report.  This situation places the candidate Below Conditions.";
      if (this.statusCode === "unknown") return "The status of this test is currently unknown.";
      if (this.statusCode === "created") return "This test has not yet been administered. A report has not been generated.";
      if (this.statusCode === "sent-by-email") return "This test has been sent by email, but not yet started. A report has not been generated.";
      if (this.statusCode === "in-progress") return "This test is currently in progress. A report has not been generated.";
      if (this.statusCode === "declined") return "The test taker declined the consent form.";
      if (this.report && this.report.summary && this.report.summary.scoreCode === "inconclusive")
        return "The test taker did not answer enough questions to generate a report.";
      return "";
    },
    title() {
      if (!this.report) return null;
      if (this.report.title) return this.report.title;
      if (this.report.registration && this.report.registration.registrationId) return `${this.report.registration.registrationId}`;
      if (this.report.referenceNumber) return `Report #${this.report.referenceNumber}`;
      return null;
    },
    getHighlightTabMouseOver() {
      if (this.hasInvestigationId) return "View the investigation highlights";
      return "View the highlights of this interview";
    },
    getHighlightTabLabel() {
      if (this.hasInvestigationId) return "Investigation Highlights";
      return "Report Highlights";
    },
  },
  created() {
    this.load();
  },
  methods: {
    getStatusClass(value) {
      if (value == "created") return "badge-info";
      if (value == "sent-by-email") return "badge-info";
      if (value == "in-progress") return "badge-warning";
      if (value == "declined") return "badge-danger";
      if (value == "completed") return "badge-success";
      return "badge-secondary";
    },
    onStarClick() {
      if (!this.report) return;

      // flip the starred value
      this.report.isStarred = !this.report.isStarred;

      // save to server
      this.$axios
        .put(`/v1/organizations/${this.organizationId}/reports/${this.reportId}/star/${this.report.isStarred.toString()}`)
        .catch(err => (this.error = err))
        .finally(() => {
          this.loadingMessage = null;
        });
    },
    load() {
      this.loadingMessage = "Loading...";
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/reports/${this.reportId}`)
        .then(res => (this.report = res.data))
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
    onLoading(message) {
      this.loadingMessage = message;
      if (!message) this.load();
    },
    onSuccess(message) {
      this.successMessage = message;
    },
    onError(error) {
      this.error = error;
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  .badge {
    font-size: 40%;
    vertical-align: super;
  }
}

.gold-star {
  color: #ffd700;
}
</style>
